import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/*
 * Scroll handling on route changes:
 *   - PUSH / REPLACE (a new page) → start at the top.
 *   - POP (browser back/forward, `history.back()`) → return to where that
 *     history entry was left.
 *
 * The browser's own restoration stays OFF: it restores once, right on
 * popstate, before React has rendered the page — and pages like the school
 * shop refetch their products on mount, so the document is still short and
 * the restore is clamped to the top. Instead positions are recorded per
 * history entry (`location.key`) and a POP re-applies the saved position every
 * frame while the page grows, until it fits, the user scrolls themselves, or
 * RESTORE_TIMEOUT_MS passes.
 *
 * Positions live in sessionStorage so they survive a reload (and the
 * page-reload navigation of the static Apidemia build).
 */

const STORAGE_KEY = 'scroll_positions';
const MAX_ENTRIES = 50;
const RESTORE_TIMEOUT_MS = 5000;
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown', 'mousedown'];

function readPositions() {
  try {
    return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
}

function savePosition(key, y) {
  const positions = readPositions();
  delete positions[key]; // re-insert last, so the oldest entries are dropped first
  positions[key] = y;
  const keys = Object.keys(positions);
  keys.slice(0, Math.max(0, keys.length - MAX_ENTRIES)).forEach((k) => delete positions[k]);
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(positions));
  } catch {
    /* storage full or blocked — restoring is best effort */
  }
}

function ScrollToTop() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const keyRef = useRef(location.key);
  const restoringRef = useRef(false);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Record the position of the current entry as the user scrolls. Recording at
  // navigation time would be too late: the next page is already in the DOM and
  // the scroll has been clamped to its height.
  useEffect(() => {
    const onScroll = () => {
      if (!restoringRef.current) savePosition(keyRef.current, window.scrollY);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Layout effect: the key must switch before the browser reports the scroll
  // the new page causes, or that scroll would be recorded for the old entry.
  useLayoutEffect(() => {
    keyRef.current = location.key;
    const target = navigationType === 'POP' ? readPositions()[location.key] : undefined;

    if (!target) {
      window.scrollTo(0, 0);
      return undefined;
    }

    restoringRef.current = true;
    const deadline = Date.now() + RESTORE_TIMEOUT_MS;
    let frame = null;

    const stop = () => {
      if (!restoringRef.current) return;
      restoringRef.current = false;
      window.cancelAnimationFrame(frame);
      USER_SCROLL_EVENTS.forEach((type) => window.removeEventListener(type, stop));
    };

    const tick = () => {
      if (Math.abs(window.scrollY - target) > 1) window.scrollTo(0, target);
      if (Date.now() >= deadline) {
        stop();
        return;
      }
      frame = window.requestAnimationFrame(tick);
    };

    USER_SCROLL_EVENTS.forEach((type) => window.addEventListener(type, stop, { passive: true }));
    tick();
    return stop;
  }, [location.key, navigationType]);

  return null;
}

export default ScrollToTop;
